// src/api/ImageRightsAPI.js
import { api } from "./client";

const BASE = "/api/admin/image-rights";

export const ImageRightsAPI = {
  /**
   * Alle Instagram-Accounts mit fremden Bildern:
   * { permissionRequired, totals, accounts: [{ handle, status, note, requestedAt, decidedAt,
   *   hostActive, agreed, visibleInProd, pictures, picturesCleared, events, upcomingEvents,
   *   upcomingCleared, clubs, nextEventDate, samples, sources: [{ host, pictures }] }] }
   */
  overview: async () => {
    const { data } = await api.get(BASE);
    return data;
  },

  /** Setzt den Stand (OPEN | REQUESTED | APPROVED | DENIED) und die Notiz eines Accounts. */
  update: async (handle, { status, note }) => {
    const { data } = await api.put(`${BASE}/${encodeURIComponent(handle)}`, { status, note });
    return data;
  },

  /**
   * Jedes fremde Bild einzeln: [{ id, title, type, account, image, source (INSTAGRAM | WEB),
   *   sourceHost, eventDate, upcoming, accountAgreed, cleared, clearedAt, clearanceNote,
   *   visibleInProd }], kommende Events zuerst. Mit account nur die Bilder dieses Accounts
   *   ('-' für Bilder ohne Account).
   */
  pictures: async (account) => {
    const { data } = await api.get(`${BASE}/pictures`, account == null ? undefined : { params: { account } });
    return data;
  },

  /** Das Bild selbst (Blob), über unsere API geholt, damit das Dashboard es in die App-Vorschau zeichnen darf. */
  pictureFile: async (image) => {
    const { data } = await api.get(`${BASE}/picture-file`, { params: { image }, responseType: 'blob' });
    return data;
  },

  /** Das Instagram-Profilbild eines Accounts (Blob) für die Host-Pille der Vorschau; null, wenn es keins gibt. */
  profilePicture: async (account) => {
    try {
      const { data } = await api.get(`${BASE}/profile-picture`, { params: { account }, responseType: 'blob' });
      return data;
    } catch {
      return null;
    }
  },

  /**
   * Löscht ein Bild bei allen Events und Clubs, die es tragen (auch aus offenen Entwürfen);
   * sie zeigen dann die Schick-Grafik. { markers: [ids] }
   */
  remove: async (image) => {
    const { data } = await api.delete(`${BASE}/pictures`, { params: { image } });
    return data;
  },

  /**
   * Gibt einzelne Bilder frei (cleared: true), sodass Nutzer sie sehen, oder versteckt sie wieder.
   * { changed, blocked }: blocked Bilder stammen von einer Blacklist-Quelle und bleiben versteckt.
   */
  clear: async (images, cleared, note) => {
    const { data } = await api.post(`${BASE}/clearances`, { images, cleared, note });
    return data;
  },

  /**
   * Whitelist und Blacklist: [{ id, kind (DOMAIN | INSTAGRAM), value, label, list (WHITE | BLACK),
   *   note, createdAt, pictures, waiting }], die Blacklist zuerst.
   */
  sources: async () => {
    const { data } = await api.get(`${BASE}/sources`);
    return Array.isArray(data) ? data : [];
  },

  /** Setzt eine Domain oder einen Instagram-Account auf eine Liste (oder verschiebt ihn). { rule, withdrawn } */
  saveSource: async ({ kind, value, list, note }) => {
    const { data } = await api.post(`${BASE}/sources`, { kind, value, list, note });
    return data;
  },

  /** Nimmt eine Quelle von ihrer Liste; ihre Bilder bleiben, wie sie sind. */
  deleteSource: async (id) => {
    await api.delete(`${BASE}/sources/${id}`);
  },

  /** Gibt Bilder von Whitelist-Quellen frei. { accepted, skipped } */
  acceptWhitelisted: async (images) => {
    const { data } = await api.post(`${BASE}/sources/accept`, { images });
    return data;
  },

  /** Löscht Bilder von Blacklist-Quellen bei allen Events und Clubs. { removed, markers, skipped } */
  removeBlacklisted: async (images) => {
    const { data } = await api.post(`${BASE}/sources/remove`, { images });
    return data;
  },

  /**
   * Unsere KI-Symbolbilder je Kategorie, mit den kommenden Events dieser Kategorie ohne eigenes
   * sichtbares Bild: { types: [{ specialType, pictures: [{ name, specialType, url, width, height,
   *   sizeBytes, createdAt, uses }], events: [{ id, title, eventDate, startTime, account, address,
   *   image, showing (NONE | HIDDEN | SYMBOL), live }] }] }. uses = kommende Events mit dem Bild.
   */
  symbolImages: async () => {
    const { data } = await api.get(`${BASE}/symbol-images`);
    return data;
  },

  /**
   * Lädt Bilder für eine Kategorie hoch. Das Backend schneidet sie auf 4:5 zu, verkleinert und
   * komprimiert sie. { added: [Bild], rejected: [{ file, reason }] }
   */
  uploadSymbolImages: async (specialType, files) => {
    const form = new FormData();
    form.append('specialType', specialType);
    [...files].forEach((file) => form.append('files', file));
    const { data } = await api.post(`${BASE}/symbol-images/pictures`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return data;
  },

  /** Entfernt ein KI-Symbolbild; Events damit zeigen wieder die Schick-Grafik. { markers: [ids] } */
  removeSymbolImage: async (name) => {
    const { data } = await api.delete(`${BASE}/symbol-images/pictures/${encodeURIComponent(name)}`);
    return data;
  },

  /** Gibt mehreren Events (z. B. allen Terminen einer Reihe) dasselbe KI-Symbolbild. Alle oder keines. [Event] */
  assignSymbolImageToAll: async (ids, image) => {
    const { data } = await api.put(`${BASE}/symbol-images/events`, { ids, image });
    return data;
  },

  /** Gibt einem Event eines der KI-Symbolbilder seiner Kategorie; sein bisheriges Bild wird ersetzt. */
  assignSymbolImage: async (id, image) => {
    const { data } = await api.put(`${BASE}/symbol-images/events/${id}`, { image });
    return data;
  },
};
